import { Link } from "react-router";
import { ArrowLeft, ClipboardList, CheckCircle, Copy, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Main } from "@/components/shared/Main";
import { Header } from "@/components/shared/Header";

export function CreateMeasurementRequestDocPage() {
  return (
    <>
      <Header />
      <Main>
        <div className="mb-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/docs" className="flex items-center">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documentation
            </Link>
          </Button>

          <div className="flex items-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mr-4">
              <ClipboardList className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Create Measurement Request</h1>
              <p className="text-xl text-gray-600">Step-by-step guide</p>
            </div>
          </div>
        </div>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              <a href="/docs/glossary#test-request" className="text-blue-600 hover:underline">
                Measurement requests
              </a>{" "}
              are used to submit measurement jobs to the{" "}
              <a href="/docs/glossary#test-engineer" className="text-blue-600 hover:underline">
                testing team
              </a>
              . They specify which{" "}
              <a href="/docs/glossary#test-label" className="text-blue-600 hover:underline">
                test labels
              </a>{" "}
              should be measured, on which wafers, and with what specifications. Creating a
              measurement request is the final step in the testing workflow.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">Prerequisites:</h4>
              <ul className="text-blue-800 text-sm">
                <li>
                  • Your role is{" "}
                  <a
                    href="/docs/glossary#project-manager"
                    className="text-blue-600 hover:underline"
                  >
                    project manager
                  </a>{" "}
                  or Admin
                </li>
                <li>
                  • At least one{" "}
                  <a href="/docs/glossary#chip-pos-files" className="text-blue-600 hover:underline">
                    chip position file
                  </a>{" "}
                  added
                </li>
                <li>
                  • At least one{" "}
                  <a href="/docs/glossary#test-label" className="text-blue-600 hover:underline">
                    test label
                  </a>{" "}
                  added
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Step-by-Step Instructions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                1
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  Navigate to Measurement Requests
                </h3>
                <p className="text-gray-700 mb-2">
                  From the main navigation, go to the Measurement Requests page to create a new
                  measurement request.
                </p>
                <Button asChild size="sm" variant="outline">
                  <Link to="/measurement-requests">Go to Measurement Requests</Link>
                </Button>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                2
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Click "Open" Button</h3>
                <p className="text-gray-700">
                  Look for the "Open" button to start creating a new measurement request.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                3
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Add Basic Information</h3>
                <p className="text-gray-700 mb-2">
                  Fill in the required basic information to unlock the next steps:
                </p>
                <ul className="text-gray-700 text-sm ml-4 space-y-1">
                  <li>
                    • <strong>Name:</strong> A descriptive name for your measurement request
                  </li>
                  <li>
                    • <strong>Project:</strong> Select the project this request belongs to
                  </li>
                  <li>
                    • <strong>Mask:</strong> Select the mask to be measured
                  </li>
                </ul>
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mt-3">
                  <p className="text-blue-800 text-sm">
                    <strong>Note:</strong> These fields are required and must be completed before
                    you can proceed to the next steps.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                4
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Add Lot Information (Optional)</h3>
                <p className="text-gray-700 mb-2">
                  Optionally, add lot information for better tracking:
                </p>
                <ul className="text-gray-700 text-sm ml-4 space-y-1">
                  <li>
                    • <strong>Imec Lot ID:</strong> Internal lot identifier
                  </li>
                  <li>
                    • <strong>Sfab Lot ID:</strong> Fab lot identifier
                  </li>
                  <li>
                    • <strong>Physical Location:</strong> Where the wafers are located
                  </li>
                  <li>
                    • <strong>Measurement Key:</strong> Unique measurement identifier
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                5
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Add Test Label</h3>
                <p className="text-gray-700 mb-2">Select the test label to be measured:</p>
                <ul className="text-gray-700 text-sm ml-4 space-y-1">
                  <li>
                    • If no test labels are linked to the mask, the "Add Test Label" button will be
                    disabled
                  </li>
                  <li>
                    • Test labels that haven't succeeded in a test flight yet will be greyed out and
                    show "(not validated)" next to them
                  </li>
                </ul>
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mt-3">
                  <p className="text-blue-800 text-sm">
                    <strong>Important:</strong> Only validated test labels can be used for
                    measurement requests.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                6
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Add Wafers to Test Label</h3>
                <p className="text-gray-700 mb-2">
                  Click the "Add Wafer" button to configure wafer measurements:
                </p>
                <ul className="text-gray-700 text-sm ml-4 space-y-1">
                  <li>
                    • <strong>Name:</strong> Select wafer name from dropdown
                  </li>
                  <li>
                    • <strong>Chippos:</strong> Select chip position from dropdown
                  </li>
                  <li>
                    • <strong>Project Spec:</strong> Select project specification from dropdown
                  </li>
                  <li>
                    • <strong>Time per Die:</strong> Manually enter the measurement time per die
                  </li>
                  <li>
                    • <strong>Stack Short:</strong> Free text field for stack description
                  </li>
                </ul>
                <div className="flex items-center space-x-2 mt-3 text-sm text-gray-600">
                  <Copy className="w-4 h-4" />
                  <span>Use the duplicate button to copy wafer settings</span>
                </div>
                <div className="flex items-center space-x-2 mt-1 text-sm text-gray-600">
                  <X className="w-4 h-4" />
                  <span>Use the X button to remove a wafer</span>
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                7
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Create Measurement Request</h3>
                <p className="text-gray-700">
                  Review all the information you've entered and click "Create" to submit your
                  measurement request. The request will be saved and made available to the testing
                  team for processing.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              Next Steps
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">After creating your measurement request, you can:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button asChild variant="outline" size="sm">
                <Link to="/measurement-requests">View All Measurement Requests</Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/docs/create-test-label">Create Additional Test Labels</Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button asChild variant="outline">
            <Link to="/docs">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documentation
            </Link>
          </Button>
          <Button asChild>
            <Link to="/measurement-requests">Go to Measurement Requests</Link>
          </Button>
        </div>
      </Main>
    </>
  );
}
